import type { ReactNode } from "react";

import { resolveUrl } from "../lib/api";
import type { Audience, Contact, ContactKind } from "../lib/types";
import { Avatar } from "./AppIcon";
import { Icon } from "./Icon";

/** Contact kinds: icon, name, input type and hint. */
export const CONTACT_KINDS: Record<ContactKind, { icon: string; name: string; input: string; placeholder: string }> = {
  email: { icon: "mail", name: "Email", input: "email", placeholder: "name@example.com" },
  phone: { icon: "phone", name: "Phone", input: "tel", placeholder: "+48 600 100 200" },
  instagram: { icon: "instagram", name: "Instagram", input: "text", placeholder: "@username or profile link" },
  tiktok: { icon: "tiktok", name: "TikTok", input: "text", placeholder: "@username or profile link" },
  facebook: { icon: "facebook", name: "Facebook", input: "text", placeholder: "username or profile link" },
  linkedin: { icon: "linkedin", name: "LinkedIn", input: "text", placeholder: "linkedin.com/in/you" },
  x: { icon: "x-social", name: "X", input: "text", placeholder: "@username" },
  youtube: { icon: "youtube", name: "YouTube", input: "text", placeholder: "@channel" },
  github: { icon: "github", name: "GitHub", input: "text", placeholder: "username" },
  telegram: { icon: "telegram", name: "Telegram", input: "text", placeholder: "@username" },
  website: { icon: "globe", name: "Website", input: "url", placeholder: "example.com" },
  other: { icon: "link", name: "Link", input: "url", placeholder: "https://…" },
};

/** Who can see a contact. One button cycles through the three. */
export const AUDIENCES: Record<Audience, { icon: string; label: string; hint: string; next: Audience }> = {
  public: { icon: "globe", label: "Public", hint: "Anyone who can see your profile", next: "friends" },
  friends: { icon: "people", label: "Friends", hint: "Only your friends", next: "private" },
  private: { icon: "lock", label: "Only me", hint: "Nobody else", next: "public" },
};

export function AudienceToggle({ value, onChange, compact }: { value: Audience; onChange: (v: Audience) => void; compact?: boolean }) {
  const a = AUDIENCES[value];
  return (
    <button
      type="button"
      className={`audience ${value}${compact ? " compact" : ""}`}
      onClick={(e) => { e.stopPropagation(); onChange(a.next); }}
      aria-label={`Visible to: ${a.label}. Tap to change to ${AUDIENCES[a.next].label}.`}
      title={a.hint}
    >
      <Icon name={a.icon} size={18} />
      {!compact && <span>{a.label}</span>}
    </button>
  );
}

/** Short text for a contact: "@anna" for socials, "anna.dev" for links. */
export function contactText(c: Contact): string {
  if (c.label) return c.label;
  if (c.kind === "email" || c.kind === "phone") return c.value;
  if (/^https?:\/\//.test(c.value)) {
    try {
      const u = new URL(c.value);
      const path = u.pathname.replace(/\/$/, "");
      return c.kind === "website" || c.kind === "other" ? `${u.hostname.replace(/^www\./, "")}${path}` : path.replace(/^\/(in\/)?@?/, "@") || u.hostname;
    } catch {
      return c.value;
    }
  }
  return `@${c.value}`;
}

/** LinkedIn-style header: background band, overlapping round photo, name. */
export function ProfileHeader({ name, handle, avatarUrl, coverUrl, onEditAvatar, onEditCover, children }: {
  name: string;
  handle: string;
  avatarUrl: string;
  coverUrl: string;
  onEditAvatar?: () => void;
  onEditCover?: () => void;
  children?: ReactNode;
}) {
  return (
    <div className="card flush profile-card">
      <div className="profile-cover">
        {coverUrl && <img src={resolveUrl(coverUrl)} alt="" />}
        {onEditCover && (
          <button className="profile-edit cover-edit" onClick={onEditCover} aria-label={coverUrl ? "Change background" : "Add background"}>
            <Icon name={coverUrl ? "pencil" : "photo"} size={18} />
          </button>
        )}
      </div>
      <div className="profile-body">
        <div className="profile-avatar">
          {onEditAvatar ? (
            <button className="avatar-ring" onClick={onEditAvatar} aria-label={avatarUrl ? "Change profile photo" : "Add profile photo"}>
              <Avatar name={name} url={avatarUrl} size={112} />
              <span className="profile-edit avatar-edit" aria-hidden="true"><Icon name="camera" size={16} /></span>
            </button>
          ) : (
            <span className="avatar-ring"><Avatar name={name} url={avatarUrl} size={112} /></span>
          )}
        </div>
        <div className="stack" style={{ gap: 2 }}>
          <span className="d" style={{ fontSize: 24, fontWeight: 700, lineHeight: 1.2 }}>{name}</span>
          <span className="m muted" style={{ fontSize: 14 }}>@{handle}</span>
        </div>
        {children}
      </div>
    </div>
  );
}

/** Contacts as others see them: tappable rows with the network's icon. */
export function ContactLinks({ contacts, showAudience }: { contacts: Contact[]; showAudience?: boolean }) {
  if (!contacts.length) return null;
  return (
    <div className="card flush">
      {contacts.map((c) => {
        const meta = CONTACT_KINDS[c.kind];
        const body = (
          <>
            <span className="contact-ic"><Icon name={meta.icon} size={20} /></span>
            <span className="grow" style={{ minWidth: 0 }}>
              <span className="ellipsis">{contactText(c)}</span>
              <span className="sub">{c.label ? `${meta.name} · ${c.value.replace(/^https?:\/\//, "")}` : meta.name}</span>
            </span>
            {showAudience && <span className="muted" title={AUDIENCES[c.visibility].hint}><Icon name={AUDIENCES[c.visibility].icon} size={18} label={AUDIENCES[c.visibility].label} /></span>}
          </>
        );
        return c.href ? (
          <a key={c.id} className="list-row" href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer noopener">{body}</a>
        ) : (
          <div key={c.id} className="list-row">{body}</div>
        );
      })}
    </div>
  );
}
